import { useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Botao, Selo, Tabela, Vazio, Medidor, type Coluna } from "@notes/ui";
import { Search, X } from "lucide-react";
import { Pagina, Secao, Carregando, Erro } from "../shell/Shell.js";
import { usarReunioes, mensagemDeErro } from "../lib/api.js";
import { dataHora, duracao } from "../lib/formato.js";
import { EstadoDaReuniao } from "./Painel.js";

type LinhaDeReuniao = {
  id: string; titulo: string; inicioEm: Date | string; duracaoS: number | null;
  status: string; tipo: string | null; plataforma: string | null; negociacaoCrmId: string | null;
};

const JANELAS = [
  { chave: "30", rotulo: "30 dias", dias: 30 },
  { chave: "90", rotulo: "90 dias", dias: 90 },
  { chave: "tudo", rotulo: "Tudo", dias: null },
] as const;

const ESTADOS = [
  { chave: "todos", rotulo: "Todas" },
  { chave: "concluida", rotulo: "Prontas" },
  { chave: "processando", rotulo: "Processando" },
  { chave: "falhou", rotulo: "Com problema" },
] as const;

/**
 * REUNIÕES. Tabela densa, porque a pergunta aqui é de comparação ("qual foi a
 * conversa da semana passada com a Camila?"), e cartão grande obriga a rolar.
 *
 * Os filtros são do CLIENTE de propósito: a lista já vem inteira do servidor
 * (limite de cinquenta), e ida ao servidor por clique de filtro deixaria a
 * tela lenta sem devolver nada em troca.
 */
export function Reunioes() {
  const q = usarReunioes();
  const navegar = useNavigate();
  const [busca, setBusca] = useState("");
  const [janela, setJanela] = useState<(typeof JANELAS)[number]["chave"]>("90");
  const [estado, setEstado] = useState<(typeof ESTADOS)[number]["chave"]>("todos");

  const todas = (q.data?.reunioes ?? []) as unknown as LinhaDeReuniao[];

  const filtradas = useMemo(() => {
    const dias = JANELAS.find((j) => j.chave === janela)?.dias ?? null;
    const corte = dias ? Date.now() - dias * 24 * 60 * 60 * 1000 : null;
    const termo = busca.trim().toLowerCase();
    return todas.filter((r) => {
      if (corte && new Date(r.inicioEm).getTime() < corte) return false;
      if (estado !== "todos" && r.status !== estado) return false;
      if (termo && !r.titulo.toLowerCase().includes(termo)) return false;
      return true;
    });
  }, [todas, busca, janela, estado]);

  if (q.isLoading) return <Pagina titulo="Reuniões"><Carregando tipo="tabela" /></Pagina>;
  if (q.isError) {
    return <Pagina titulo="Reuniões"><Erro mensagem={mensagemDeErro(q.error)} aoTentarDeNovo={() => void q.refetch()} /></Pagina>;
  }

  const colunas: Array<Coluna<LinhaDeReuniao>> = [
    {
      chave: "titulo", cabecalho: "Conversa", ordenarPor: (r) => r.titulo,
      celula: (r) => (
        <span className="enotes-tb-titulo">
          <span className="enotes-tb-nome">{r.titulo}</span>
          {r.negociacaoCrmId && <Selo tom="marca">negociação {r.negociacaoCrmId}</Selo>}
        </span>
      ),
    },
    {
      chave: "quando", cabecalho: "Quando", largura: "11rem",
      ordenarPor: (r) => new Date(r.inicioEm).getTime(),
      celula: (r) => <span className="carimbo">{dataHora(r.inicioEm)}</span>,
    },
    {
      chave: "duracao", cabecalho: "Duração", largura: "7rem", alinhamento: "fim",
      ordenarPor: (r) => r.duracaoS ?? 0,
      celula: (r) => <span className="num">{duracao(r.duracaoS)}</span>,
    },
    {
      chave: "onde", cabecalho: "Onde", largura: "8rem",
      ordenarPor: (r) => r.plataforma ?? "",
      celula: (r) => <span className="carimbo">{r.plataforma ?? "sem registro"}</span>,
    },
    {
      chave: "estado", cabecalho: "Estado", largura: "9rem",
      ordenarPor: (r) => r.status,
      celula: (r) => <EstadoDaReuniao status={r.status} />,
    },
  ];

  return (
    <Pagina titulo="Reuniões" descricao="Tudo que foi gravado, transcrito e entendido." largura="larga">
      <Secao ordem={0}>
        <div className="enotes-filtros">
          <div className="enotes-busca-campo">
            <Search size={14} aria-hidden />
            <input
              className="enotes-busca-entrada" value={busca} onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar pelo nome da conversa" aria-label="Buscar pelo nome da conversa"
            />
            {busca && (
              <button className="enotes-icone" onClick={() => setBusca("")} aria-label="Limpar a busca"><X size={13} /></button>
            )}
          </div>
          <div className="enotes-chips" role="group" aria-label="Período">
            {JANELAS.map((j) => (
              <button key={j.chave} className="enotes-chip" data-ativo={janela === j.chave || undefined}
                onClick={() => setJanela(j.chave)}>{j.rotulo}</button>
            ))}
          </div>
          <div className="enotes-chips" role="group" aria-label="Estado">
            {ESTADOS.map((e) => {
              const quantas = e.chave === "todos" ? todas.length : todas.filter((r) => r.status === e.chave).length;
              if (quantas === 0 && e.chave !== "todos") return null;
              return (
                <button key={e.chave} className="enotes-chip" data-ativo={estado === e.chave || undefined}
                  onClick={() => setEstado(e.chave)}>
                  {e.rotulo}<span className="num enotes-chip-num">{quantas}</span>
                </button>
              );
            })}
          </div>
        </div>
      </Secao>

      <Secao ordem={1}>
        <Tabela
          colunas={colunas}
          linhas={filtradas}
          chave={(r) => r.id}
          aoClicar={(r) => void navegar({ to: "/reunioes/$id", params: { id: r.id } })}
          vazio={
            todas.length === 0 ? (
              <Vazio titulo="Nenhuma reunião ainda"
                descricao="Conecte sua agenda ou envie um áudio para a primeira conversa entrar aqui."
                acao={<Botao variante="primario" tamanho="sm" onClick={() => void navegar({ to: "/gravar" })}>Configurar a gravação</Botao>} />
            ) : (
              <Vazio titulo="Nenhuma conversa com esse filtro"
                descricao="Tente outro período, outro estado, ou limpe a busca."
                acao={<Botao tamanho="sm" onClick={() => { setBusca(""); setJanela("tudo"); setEstado("todos"); }}>Limpar os filtros</Botao>} />
            )
          }
        />
        {filtradas.length > 0 && (
          <p className="enotes-tabela-rodape carimbo">
            {filtradas.length} de {todas.length} conversas
          </p>
        )}
      </Secao>
    </Pagina>
  );
}

export { Medidor as MedidorDaLista };
